import React from 'react';

import { useIntl } from 'react-intl';

import { RESULTS_SORT_FIELDS } from '@atlassian/search-client/results-sort';

import { ProductKeys } from '../../../../../common/constants/products';
import { FilterProductIcon } from '../../../../../common/ui/filters/filter-product-icon';
import type { FilterProps } from '../../../../../common/ui/filters/product-filters/types';
import SelectFilter, {
	type SelectFilterOption,
} from '../../../../../common/ui/filters/select-filter';
import { useSelectFilter } from '../../../../../controllers/filters/base-select-filter';
import {
	useFiltersDiscoverability2Enabled,
	usePrimaryProduct,
} from '../../../../../controllers/store/bootstrap';
import { useSelectedProduct } from '../../../../../controllers/store/search';
import { filter as sortFilter } from '../../../../../controllers/store/search/filters/sort';

import { messages } from './messages';

const ResultsSortNew = ({
	isFilterInDropdown,
	showPrimaryProductIcon,
	fit = 'default',
	onFilterChange,
	hideOnHoverOut,
	onClearOptions,
}: FilterProps) => {
	const { formatMessage } = useIntl();

	const [primaryProduct] = usePrimaryProduct();
	const isFiltersDiscoverability2Enabled = useFiltersDiscoverability2Enabled();
	const [selectedProduct] = useSelectedProduct();

	const [filterData, { loadInitialOptions, setOption, clearOptions }] = useSelectFilter({
		filterId: sortFilter.id,
	});

	// Do not show if the select product is not Confluence or Jira
	if (!(selectedProduct === ProductKeys.Confluence || selectedProduct === ProductKeys.Jira)) {
		return null;
	}

	if (!filterData) {
		return null;
	}

	const { isLoading, selectedOptions, options } = filterData;

	const selectedLabelKey = selectedOptionToSelectedLabelKey(selectedOptions[0]);
	const label = selectedLabelKey
		? formatMessage(messages[selectedLabelKey])
		: formatMessage(messages[RESULTS_SORT_FIELDS.relevanceDESC.selectedLabelKey]);

	return (
		<SelectFilter
			contentFit={fit}
			onDropdownOpen={loadInitialOptions}
			onSelectChange={(option, ...restArgs) => {
				setOption(option, ...restArgs);
				onFilterChange?.(option);
			}}
			options={options}
			selectedOptions={selectedOptions}
			label={label}
			filterOrControlId={sortFilter.id}
			isLoading={isLoading}
			isFilterInDropdown={isFilterInDropdown}
			isFiltersDiscoverability2Enabled={isFiltersDiscoverability2Enabled}
			iconBefore={
				<FilterProductIcon
					showPrimaryProductIcon={showPrimaryProductIcon}
					primaryProduct={primaryProduct}
				/>
			}
			hideOnHoverOut={hideOnHoverOut}
			onClearOptions={() => {
				clearOptions();
				onClearOptions?.({ filterId: sortFilter.id });
			}}
			getFirstSelectedLabel={() => ''}
		/>
	);
};

export const ResultsSort = (props: FilterProps): React.JSX.Element => {
	return <ResultsSortNew {...props} />;
};

const selectedOptionToSelectedLabelKey = (option: SelectFilterOption) => {
	switch (option?.value) {
		case 'relevance':
			return RESULTS_SORT_FIELDS.relevanceDESC.selectedLabelKey;
		case 'updatedNewest':
			return RESULTS_SORT_FIELDS.latestModifiedDateDESC.selectedLabelKey;
		case 'updatedOldest':
			return RESULTS_SORT_FIELDS.latestModifiedDateASC.selectedLabelKey;
		case 'createdNewest':
			return RESULTS_SORT_FIELDS.createdDateDESC.selectedLabelKey;
		case 'createdOldest':
			return RESULTS_SORT_FIELDS.createdDateASC.selectedLabelKey;
	}
};
